/**
 * スポット詳細情報表示コンポーネント
 * Google Places APIから取得した詳細情報を常に表示
 */

import React, { useState, useEffect } from 'react';
import { Spot } from '../types/core';

export interface SpotDetailsProps {
    spot: Spot;
    className?: string;
}

interface PlaceDetails {
    website?: string;
    phoneNumber?: string;
    rating?: number;
    userRatingCount?: number;
    priceLevel?: number;
    businessStatus?: string;
    openingHours?: {
        openNow?: boolean;
        weekdayText?: string[];
    };
}

export const SpotDetails: React.FC<SpotDetailsProps> = ({
    spot,
    className = ''
}) => {
    const [details, setDetails] = useState<PlaceDetails | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasAttempted, setHasAttempted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Google Places APIから詳細情報を取得（常に実行）
    useEffect(() => {
        if (!details && !isLoading && !hasAttempted) {
            setIsLoading(true);
            setHasAttempted(true);
            setError(null);

            console.log('Fetching details for spot:', spot.name, spot.location);
            console.log('Spot source:', spot.source, 'Source ID:', spot.sourceId);

            // まずPlace IDを取得
            const searchParams = new URLSearchParams({
                name: spot.name,
                lat: spot.location.lat.toString(),
                lng: spot.location.lng.toString(),
                radius: '500'
            });

            const searchUrl = `https://flaato-api.flaato-app.workers.dev/api/places/search?${searchParams}`;
            console.log('Search URL:', searchUrl);

            fetch(searchUrl)
                .then(response => {
                    console.log('Search response status:', response.status);
                    if (!response.ok) {
                        throw new Error(`Search API error! status: ${response.status}`);
                    }
                    return response.json();
                })
                .then((data: any) => {
                    console.log('Search response data:', data);
                    if (data?.place?.placeId) {
                        console.log('Found place ID:', data.place.placeId);
                        // Place Detailsを取得
                        const detailsUrl = `https://flaato-api.flaato-app.workers.dev/api/places/details?placeId=${data.place.placeId}`;
                        console.log('Details URL:', detailsUrl);

                        return fetch(detailsUrl)
                            .then(response => {
                                console.log('Details response status:', response.status);
                                if (!response.ok) {
                                    throw new Error(`Details API error! status: ${response.status}`);
                                }
                                return response.json();
                            });
                    } else {
                        console.log('No place ID found in search response, trying alternative search');
                        // 施設名の一部で再検索を試行
                        const simplifiedName = spot.name.replace(/[・＆&]/g, ' ').split(' ')[0];
                        if (simplifiedName !== spot.name && simplifiedName.length > 2) {
                            const altSearchParams = new URLSearchParams({
                                name: simplifiedName,
                                lat: spot.location.lat.toString(),
                                lng: spot.location.lng.toString(),
                                radius: '500'
                            });
                            const altSearchUrl = `https://flaato-api.flaato-app.workers.dev/api/places/search?${altSearchParams}`;
                            console.log('Alternative search URL:', altSearchUrl);

                            return fetch(altSearchUrl)
                                .then(response => response.json())
                                .then((altData: any) => {
                                    console.log('Alternative search response:', altData);
                                    if (altData?.place?.placeId) {
                                        return fetch(`https://flaato-api.flaato-app.workers.dev/api/places/details?placeId=${altData.place.placeId}`)
                                            .then(response => response.json());
                                    }
                                    return Promise.resolve({ place: null });
                                });
                        }
                        // Place IDが見つからない場合
                        return Promise.resolve({ place: null });
                    }
                })
                .then((detailData: any) => {
                    console.log('Details response data:', detailData);
                    const place = detailData?.place;

                    // 取得できた情報だけを設定（部分的でも表示）
                    const newDetails: PlaceDetails = {};

                    if (place) {
                        if (place.website) newDetails.website = place.website;
                        if (place.phoneNumber) newDetails.phoneNumber = place.phoneNumber;
                        if (place.rating) newDetails.rating = place.rating;
                        if (place.userRatingCount) newDetails.userRatingCount = place.userRatingCount;
                        if (place.priceLevel) newDetails.priceLevel = place.priceLevel;
                        if (place.businessStatus) newDetails.businessStatus = place.businessStatus;
                        if (place.openingHours) newDetails.openingHours = place.openingHours;
                    }

                    console.log('Processed details:', newDetails);

                    // Google Places APIから情報が取得できた場合
                    if (Object.keys(newDetails).length > 0) {
                        setDetails(newDetails);
                        setError(null);
                    } else {
                        // Google Places APIで見つからない場合、基本情報を表示
                        const fallbackDetails: PlaceDetails = {};

                        // スポットの基本情報から利用可能な情報を構築
                        // 営業状況の表示
                        if (spot.openStatus && spot.openStatus !== 'unknown_no_data') {
                            fallbackDetails.businessStatus = spot.openStatus === 'open' ? 'OPERATIONAL' : 'CLOSED';
                        }

                        setDetails(fallbackDetails);
                        setError(null);
                    }
                })
                .catch(error => {
                    console.error('Failed to fetch place details for:', spot.name, error);
                    setError(`詳細情報の取得に失敗しました: ${error.message}`);
                    setDetails(null);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [spot.name, spot.location.lat, spot.location.lng]); // 依存関係を適切に設定

    const getPriceLevelText = (level?: number): string => {
        if (!level) return '';
        const levels = ['', '¥', '¥¥', '¥¥¥', '¥¥¥¥'];
        return levels[level] || '';
    };

    return (
        <div className={className}>
            {/* 詳細情報を常に表示 */}
            <div className="mt-2 p-3 bg-gray-50 rounded-md text-xs text-left">
                {isLoading ? (
                    <div className="text-gray-500 text-left">読み込み中...</div>
                ) : details && Object.keys(details).length > 0 ? (
                    <div className="space-y-2">
                        {/* ウェブサイト */}
                        {details.website && (
                            <div className="text-left break-words">
                                <span className="font-medium text-gray-700">HP: </span>
                                <a
                                    href={details.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline break-all"
                                >
                                    公式サイト
                                </a>
                            </div>
                        )}

                        {/* 電話番号 */}
                        {details.phoneNumber && (
                            <div className="text-left break-words">
                                <span className="font-medium text-gray-700">TEL: </span>
                                <a
                                    href={`tel:${details.phoneNumber}`}
                                    className="text-blue-600 hover:text-blue-800 break-all"
                                >
                                    {details.phoneNumber}
                                </a>
                            </div>
                        )}

                        {/* 評価 */}
                        {details.rating && (
                            <div className="text-left">
                                <span className="font-medium text-gray-700">評価: </span>
                                <span className="text-yellow-600">★{details.rating.toFixed(1)}</span>
                                {details.userRatingCount && (
                                    <span className="text-gray-500 ml-1">({details.userRatingCount}件)</span>
                                )}
                            </div>
                        )}

                        {/* 価格帯 */}
                        {details.priceLevel && (
                            <div className="text-left">
                                <span className="font-medium text-gray-700">価格帯: </span>
                                <span className="text-green-600">{getPriceLevelText(details.priceLevel)}</span>
                            </div>
                        )}

                        {/* 営業状況 */}
                        {details.businessStatus && (
                            <div className="text-left">
                                <span className="font-medium text-gray-700">営業状況: </span>
                                <span className={`${details.businessStatus === 'OPERATIONAL' ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                    {details.businessStatus === 'OPERATIONAL' ? '営業中' : '営業停止'}
                                </span>
                            </div>
                        )}

                        {/* 営業時間（今日の営業状況） */}
                        {details.openingHours && (
                            <div className="text-left">
                                <span className="font-medium text-gray-700">営業: </span>
                                <span className={`${details.openingHours.openNow ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                    {details.openingHours.openNow ? '営業中' : '営業時間外'}
                                </span>
                            </div>
                        )}

                        {/* カテゴリ情報 */}
                        {spot.categoryJa && (
                            <div className="text-left break-words">
                                <span className="font-medium text-gray-700">カテゴリ: </span>
                                <span className="text-gray-600">{spot.categoryJa}</span>
                            </div>
                        )}

                        {/* 営業時間情報（OSM形式） */}
                        {spot.openingHours && (
                            <div className="text-left break-words">
                                <span className="font-medium text-gray-700">営業時間: </span>
                                <span className="text-gray-600">{spot.openingHours}</span>
                            </div>
                        )}

                        {/* Google Mapsで検索 */}
                        <div className="text-left break-words">
                            <span className="font-medium text-gray-700">地図: </span>
                            <a
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(spot.name)}&query_place_id=${spot.location.lat},${spot.location.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline break-all"
                            >
                                Google Mapsで検索
                            </a>
                        </div>
                    </div>
                ) : hasAttempted ? (
                    <div className="space-y-2 text-left">
                        {/* 基本情報を常に表示 */}
                        {spot.categoryJa && (
                            <div className="text-left break-words">
                                <span className="font-medium text-gray-700">カテゴリ: </span>
                                <span className="text-gray-600">{spot.categoryJa}</span>
                            </div>
                        )}

                        {/* 営業状況 */}
                        {spot.openStatus && spot.openStatus !== 'unknown_no_data' && (
                            <div className="text-left">
                                <span className="font-medium text-gray-700">営業状況: </span>
                                <span className={`${spot.openStatus === 'open' ? 'text-green-600' : spot.openStatus === 'closed' ? 'text-red-600' : 'text-gray-600'}`}>
                                    {spot.openStatus === 'open' ? '営業中' :
                                        spot.openStatus === 'closed' ? '営業時間外' :
                                            '営業状況不明'}
                                </span>
                            </div>
                        )}

                        {/* 営業時間情報（OSM形式） */}
                        {spot.openingHours && (
                            <div className="text-left break-words">
                                <span className="font-medium text-gray-700">営業時間: </span>
                                <span className="text-gray-600">{spot.openingHours}</span>
                            </div>
                        )}

                        {/* 予算情報 */}
                        {spot.budget && (
                            <div className="text-left">
                                <span className="font-medium text-gray-700">予算: </span>
                                <span className="text-green-600">
                                    ¥{spot.budget.min.toLocaleString()}-{spot.budget.max.toLocaleString()}
                                </span>
                            </div>
                        )}

                        {/* Google Mapsで検索 */}
                        <div className="text-left break-words">
                            <span className="font-medium text-gray-700">地図: </span>
                            <a
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(spot.name)}&query_place_id=${spot.location.lat},${spot.location.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline break-all"
                            >
                                Google Mapsで検索
                            </a>
                        </div>

                        {/* データソース情報 */}
                        <div className="text-xs text-gray-400 mt-2 text-left break-words">
                            データ提供: {spot.source === 'hotpepper' ? 'ホットペッパー' :
                                spot.source === 'yolp' ? 'Yahoo!ロコ' :
                                    spot.source === 'osm' ? 'OpenStreetMap' :
                                        spot.source === 'doorkeeper' ? 'Doorkeeper' : spot.source}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default SpotDetails;